import "./estimate-form.css";
import { useMemo, useState, useEffect, useCallback, useRef } from "react";
import editIcon from "../../../assets/componentsIcons/dropdown/edit.svg";
import deleteIcon from "../../../assets/componentsIcons/dropdown/delete.svg";
import unitIcon from "../../../assets/componentsIcons/dropdown/unit.svg";
import ActionMenu from "../../../newComponents/actionMenus/action-menu";
import dropdownIcon from "../../../assets/componentsIcons/dropdown/dropdown.svg";
import {
  AssociateContractorVM,
  DocumentEntityTypesEnum,
  DocumentTypesEnum,
  LookupVM,
  MarkAsFixedServiceRequestVM,
  ServiceRequestService,
  ServiceRequestStatusesEnum,
} from "@propertelligent/client-api";
import apiHelper from "../../../utils/apiHelper";
import { ConfirmationPopup } from "../../../newComponents/confirmation-popup/confirmation-popup-demo";
import RightSideModal from "../../../newComponents/Modal/right-side-modal";
import { FormProvider, useForm } from "react-hook-form";
import Card from "../../../newComponents/card";
import InputField from "../../../newComponents/form-fields/InputField";
import DescriptionTextarea from "../../../newComponents/form-fields/DescriptionTextarea";
import SelectField from "../../../newComponents/form-fields/SelectField";
import DocumentPhotoUploader from "../DocumentPhotoUploader";
import SimpleButton from "../../../newComponents/buttons/Simple-button";
import EmptyState from "../../../newComponents/emptystate";

import estimateIcon from "../../../assets/estimate.svg";
import SimpleSpinner from "../../../components/loader";
import { GetImages } from "../../../utils/GetImages";
import SimpleOutlineButton from "../../../newComponents/buttons/Simple-Outline-button";
import CurrencyInput from "../../../newComponents/form-fields/CurrencyInput";
import CurrencyTooltip from "../../../components/currencytooltip/CurrencyTooltip";
import { useCurrencySymbol } from "../../../hooks/useCurrencySymbol";
import { usePermissions } from "../../../hooks/usePermissions";

export interface DocumentPhotoUploaderRef {
  addBase64Image: (base64: string, filename?: string) => Promise<void>;
  refreshDocuments: () => Promise<void>;
}

interface EstimateFormProps {
  inHouse?: boolean;
  showAssignmentView: boolean;
  setShowAssignmentView: (show: boolean) => void;
  serviceRequestId: number;
  editMode?: boolean;
  editData?: {
    laborCost?: string;
    materialCost?: string;
    fixDate?: string;
    fixNotes?: string;
  };
  formattedData?: any;
  onEditSubmit?: (data: any) => void;
  onMarkAsFixedSuccess?: () => void;
  onReassign?: () => void;
  serviceRequestStatus?: any;
  assignedToName?: string;
  // New props for display view
  showInProgressView?: boolean;
  isVerified?: boolean;
  workPhotos?: string[];
  isLoading?: boolean;
  onEditEstimateClick?: () => void;
  actionButtonLabel?: string;
  onVerifyAction?: () => void;
}

interface EstimateFormData {
  estimatedCost?: string;
  contractor?: string;
  estimateAmount?: string;
  laborCost?: string;
  materialCost?: string;
  fixDate?: string;
  fixNotes?: string;
}

interface EditEstimateFormData {
  contractorName?: string;
  estimatedCost?: string;
}

export default function EstimateForm({
  inHouse,
  showAssignmentView,
  setShowAssignmentView,
  serviceRequestId,
  editMode = false,
  editData,
  onEditSubmit,
  onMarkAsFixedSuccess,
  onReassign,
  serviceRequestStatus,
  assignedToName,
  // Destructure new props
  showInProgressView,
  isVerified,
  workPhotos = [],
  isLoading: isPageLoading,
  onEditEstimateClick,
  actionButtonLabel,
  onVerifyAction,
  formattedData,
}: EstimateFormProps) {
  const [assignedContractor, setAssignedContractor] =
    useState<AssociateContractorVM | null>(null);
  const [associatedContractors, setAssociatedContractors] = useState<
    AssociateContractorVM[]
  >([]);
  const [contractorsLookup, setContractorsLookup] = useState<LookupVM[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingEstimate, setEditingEstimate] =
    useState<AssociateContractorVM | null>(null);
  const [showConfirmation, setShowConfirmation] = useState<number | null>(null);
  const [curentServiceRequestStatus, setcurentServiceRequestStatus] =
    useState<any | null>(null);
  const [statusLoaded, setStatusLoaded] = useState(false);

  const photoUploaderRef = useRef<DocumentPhotoUploaderRef>(null);


  const currencySymbol = useCurrencySymbol();
  const { canWrite } = usePermissions();

  // Form for Add Estimate
  const addEstimateMethods = useForm<EstimateFormData>({
    defaultValues: {
      contractor: "",
      estimateAmount: "",
      laborCost: "",
      materialCost: "",
      fixDate: "",
      fixNotes: "",
    },
  });

  // Separate form for Edit Estimate modal
  const editEstimateMethods = useForm<EditEstimateFormData>({
    defaultValues: {
      contractorName: "",
      estimatedCost: "",
    },
  });

  const {
    reset: resetAddForm,
    handleSubmit: handleAddSubmit,
    formState: { errors: addErrors, isDirty: isAddDirty },
    setValue: setAddValue,
    watch: watchAdd,
  } = addEstimateMethods;
  const {
    reset: resetEditForm,
    handleSubmit: handleEditSubmit,
    setValue: setEditValue,
  } = editEstimateMethods;

  const selectedContractor = watchAdd("contractor");
  const estimateAmount = watchAdd("estimateAmount");

  // Update form when editData values actually change (not on every render)
  useEffect(() => {
    if (editData) {
      setAddValue("laborCost", editData.laborCost);
      setAddValue("materialCost", editData.materialCost);
      setAddValue(
        "fixDate",
        editData.fixDate
          ? new Date(editData.fixDate).toISOString().split("T")[0]
          : "",
      );
      setAddValue("fixNotes", editData.fixNotes);
    }
  }, [
    editData?.laborCost,
    editData?.materialCost,
    editData?.fixDate,
    editData?.fixNotes,
    setAddValue,
  ]);

  // ✅ NEW: Fetch assigned contractor when in edit mode
  useEffect(() => {
    const fetchAssignedContractor = async () => {
      if (editMode || showInProgressView) {
        // Also fetch for display view
        setIsLoading(true);
        try {
          const response = await apiHelper(
            ServiceRequestService.getApiServiceRequest1,
            { showNotification: false },
            serviceRequestId,
          );

          if (response?.data?.success) {
            const data = response.data.success;

            // Check if there's an assigned contractor
            if (data.id) {
              // Fetch contractor details from associated contractors
              const contractorsResponse = await apiHelper(
                ServiceRequestService.getApiServiceRequestAssociatedContractors,
                { showNotification: false },
                serviceRequestId,
              );

              if (contractorsResponse?.data?.success) {
                setAssignedContractor(
                  contractorsResponse?.data?.success[0] || null,
                );
              }
            }
          }
        } catch (err) {
          console.error("Failed to fetch assigned contractor", err);
        } finally {
          setIsLoading(false);
        }
      }
    };

    fetchAssignedContractor();
  }, [editMode, showInProgressView, serviceRequestId]);

  const fetchContractorsData = useCallback(async () => {
    if (!editMode) {
      setIsLoading(true);
      setError(null);
      try {
        const lookupResponse = await apiHelper(
          ServiceRequestService.getApiServiceRequestAssociatedContractorsLookup,
          { showNotification: false },
          serviceRequestId,
        );
        if (lookupResponse?.data?.success) {
          setContractorsLookup(lookupResponse.data.success);
        }

        const contractorsResponse = await apiHelper(
          ServiceRequestService.getApiServiceRequestAssociatedContractors,
          { showNotification: false },
          serviceRequestId,
        );
        if (contractorsResponse?.data?.success) {
          setAssociatedContractors(contractorsResponse.data.success);
        }
      } catch (err) {
        setError("Failed to fetch contractors data");
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    }
  }, [serviceRequestId, editMode]);

  useEffect(() => {
    fetchContractorsData();
  }, [fetchContractorsData]);

  // Load default values from API
  useEffect(() => {
    const fetchDetails = async () => {
      setIsLoading(true);
      try {
        const response = await apiHelper(
          ServiceRequestService.getApiServiceRequest1,
          { showNotification: false },
          serviceRequestId,
        );

        if (response?.data?.success) {
          const data = response.data.success;
          setcurentServiceRequestStatus(data.serviceRequestStatus);
          setStatusLoaded(true);

          resetAddForm({
            laborCost: data.laborCost?.toString() || "",
            materialCost: data.materialCost?.toString() || "",
            fixDate: data.completeDate
              ? new Date(data.completeDate).toISOString().split("T")[0]
              : "",
            fixNotes: data.fixNotes || "",
          });
        }
      } catch (err) {
        setError("Failed to load service request details");
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchDetails();
  }, [serviceRequestId, resetAddForm]);

  const handleAddEstimate = async () => {
    if (!selectedContractor || !estimateAmount) return;

    setIsLoading(true);
    setError(null);

    try {
      const contractor = contractorsLookup.find(
        (c) => c.id?.toString() === selectedContractor.toString(),
      );
      if (contractor) {
        const newEstimate: AssociateContractorVM = {
          serviceRequestId,
          userId: contractor.id,
          contractorName: contractor.name,
          estimatedCost: parseFloat(estimateAmount),
        };

        const response = await apiHelper(
          ServiceRequestService.postApiServiceRequestAssociate,
          {
            title: "Estimate Added",
            successMessage: "Estimate added successfully",
            showNotification: true,
            successIcon: (
              <img
                src={GetImages.toastEstimateIcon}
                alt="success"
                width="20"
                height="20"
              />
            ),
          },
          newEstimate,
        );

        if (response?.data?.success) {
          await fetchContractorsData();
          setAddValue("contractor", "");
          setAddValue("estimateAmount", "");
        }
      }
    } catch (err) {
      setError("Failed to add estimate");
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditEstimate = (estimate: AssociateContractorVM) => {
    setEditingEstimate(estimate);
    setEditValue("contractorName", estimate.contractorName || "");
    setEditValue("estimatedCost", estimate.estimatedCost?.toString() || "");
    setIsEditModalOpen(true);
  };

  const handleUpdateEstimate = async (data: EditEstimateFormData) => {
    if (!editingEstimate) return;

    setIsLoading(true);
    setError(null);

    try {
      const updatedEstimate: AssociateContractorVM = {
        id: editingEstimate.id,
        serviceRequestId: serviceRequestId,
        userId: editingEstimate.userId,
        contractorName: editingEstimate.contractorName,
        estimatedCost: parseFloat(data.estimatedCost || "0"),
      };

      const response = await apiHelper(
        ServiceRequestService.postApiServiceRequestAssociate,
        {
          showNotification: true,
          title: "Contractor Updated",
          successMessage: "Contractor Updated Successfully",
          successIcon: (
            <img
              src={GetImages.toastContractorIcon}
              alt="success"
              width="20"
              height="20"
            />
          ),
        },
        updatedEstimate,
      );

      if (response?.data?.success) {
        await fetchContractorsData();
        setIsEditModalOpen(false);
        setEditingEstimate(null);
        resetEditForm();
      }
    } catch (err) {
      setError("Failed to update estimate");
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (associateContractorId: number) => {
    setIsLoading(true);
    setError(null);

    try {
      await apiHelper(
        ServiceRequestService.deleteApiServiceRequestAssociate,
        {
          showNotification: true,
          title: "Estimate Deleted",
          successMessage: "Estimate deleted Successfully",
          successIcon: (
            <img
              src={GetImages.toastEstimateIcon}
              alt="success"
              width="20"
              height="20"
            />
          ),
        },
        serviceRequestId,
        associateContractorId,
      );
      await fetchContractorsData();
      setShowConfirmation(null);
    } catch (err) {
      setError("Failed to delete estimate");
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  // ✅ UPDATED: Call schedule fix API when assigning contractor
  const handleAssignContractor = async (contractor: AssociateContractorVM) => {
    setIsLoading(true);
    setError(null);

    try {
      // Call the schedule fix API
      const response = await apiHelper(
        ServiceRequestService.putApiServiceRequestSchedulefix,
        {
          title: "Contractor Assigned",
          successMessage: "Contractor assigned successfully",
          successIcon: (
            <img
              src={GetImages.toastContractorIcon}
              alt="success"
              width="20"
              height="20"
            />
          ),
          showNotification: true,
        },
        serviceRequestId,
        contractor.id!,
      );

      if (response?.data?.success) {
        setAssignedContractor(contractor);
        setShowAssignmentView(true);

        // ✅ Trigger parent refresh to update stepper and status
        if (onMarkAsFixedSuccess) {
          await onMarkAsFixedSuccess();
        }
      }
    } catch (err) {
      setError("Failed to assign contractor");
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditReassign = () => {
    setShowAssignmentView(false);
    setAssignedContractor(null);
    setAddValue("laborCost", "");
    setAddValue("materialCost", "");
    setAddValue("fixDate", "");
    setAddValue("fixNotes", "");
    if (onReassign) {
      onReassign(); // ✅ NEW: Call parent handler to reset step and status
    }
  };

  const onSubmit = async (formData: EstimateFormData) => {

    if (editMode && onEditSubmit) {
      onEditSubmit(formData);
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const requestBody: MarkAsFixedServiceRequestVM = {
        fixDate: new Date(formData.fixDate).toISOString(),
        laborCost: formData.laborCost ? parseFloat(formData.laborCost) : null,
        materialCost: formData.materialCost
          ? parseFloat(formData.materialCost)
          : null,
        fixNotes: formData.fixNotes || null,
      };

      const response = await apiHelper(
        ServiceRequestService.putApiServiceRequestMarkAsFixed,
        {
          showNotification: true,
          title: "Marked As Fixed",
          successMessage: "Service Request marked as fixed",
          successIcon: (
            <img
              src={GetImages.toastServiceRequestIcon}
              alt="success"
              width="20"
              height="20"
            />
          ),
        },
        serviceRequestId,
        requestBody,
      );

      if (response?.data?.success) {
        if (onMarkAsFixedSuccess) {
          onMarkAsFixedSuccess();
        } else {
          setShowAssignmentView(false);
        }
      }
    } catch (err) {
      setError("Failed to mark as fixed");
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const onError = (formErrors: any) => {
    console.log("Form validation errors >>>", formErrors);
  };

  const menuItems = useMemo(() => {
    const items = [];

    if (canWrite("ServiceRequest")) {
      items.push({
        icon: unitIcon,
        label: "Assign Contractor",
        action: (id: number) => {
          const estimate = associatedContractors.find((e) => e.id == id);
          if (estimate) handleAssignContractor(estimate);
        },
        isDivider: false,
        isDestructive: false,
      });

      items.push({ isDivider: true });

      items.push({
        icon: editIcon,
        label: "Edit",
        action: (id: number) => {
          const estimate = associatedContractors.find((e) => e.id == id);
          if (estimate) handleEditEstimate(estimate);
        },
        isDivider: false,
        isDestructive: false,
      });

      items.push({ isDivider: true });

      items.push({
        icon: deleteIcon,
        label: "Delete",
        action: (id: number) => setShowConfirmation(Number(id)),
        isDivider: false,
        isDestructive: true,
      });
    }

    // Remove any trailing dividers
    while (items.length > 0 && items[items.length - 1].isDivider) {
      items.pop();
    }

    return items;
  }, [associatedContractors, canWrite]);

  const renderWorkDetailsForm = () => (
    <div className="work-details-form">
      <div className="work-details-header">
        <h3>Work Details</h3>
      </div>
      <div className="work-details-content">
        <div className="work-details-content-left">
          <div className="form-row">
            <CurrencyInput
              name="laborCost"
              label="Labor Cost"
              required={true}
              requiredMessage="Labor cost is required"
              placeholder="Enter Labor Cost"
              className="form-group"
              disabled={isLoading}
            />
            <CurrencyInput
              name="materialCost"
              label="Material Cost"
              required={true}
              requiredMessage="Material cost is required"
              placeholder="Enter Material Cost"
              className="form-group"
              disabled={isLoading}
            />
            <InputField
              name="fixDate"
              label="Fix Date"
              type="date"
              required={true}
              requiredMessage="Fix date is required"
              placeholder="Enter Fix Date"
              className="form-group"
            />
          </div>
          <DescriptionTextarea
            name="fixNotes"
            label="Fix Notes"
            required={true}
            requiredMessage="Fix notes are required"
            placeholder="Enter Fix Notes"
            rows={4}
            className="form-group full-width"
            validation={{
              maxLength: {
                value: 250,
                message: "Fix Notes cannot exceed 250 characters",
              },
            }}
          />
        </div>
        <div className="work-details-content-right">
          <DocumentPhotoUploader
            ref={photoUploaderRef}
            entityId={serviceRequestId}
            entityType={DocumentEntityTypesEnum.ServiceRequest}
            documentType={DocumentTypesEnum.AfterImage}
            label="Add Fix Photos"
            maxImages={10}
            maxSizeInMB={1}
            required={false}
            onImagesChange={(images) => {
              console.log("Images changed:", images);
            }}
            onDocumentsStatusChange={(hasDocuments) => {
              console.log("Has documents:", hasDocuments);
            }}
          />
        </div>
      </div>
    </div>
  );

  const renderContractorInfo = () => {
    const shouldShowEditReassign = [
      "Open",
      "Estimating",
      "In Progress",
    ].includes(serviceRequestStatus || "");

    return (
      <div className="contractor-info">
        <div className="contractor-item">
          <div className="contractor-icon">
            <img
              src={GetImages.SR_ContractorIcon}
              alt="success"
              width="26"
              height="26"
              className="iconStyleWithWhiteFilter"
            />
          </div>
          <div>
            <div className="contractor-label">Contractor Name</div>
            <div className="contractor-value">
              {assignedToName || "Not Assigned"}
            </div>
          </div>
        </div>
        <div className="contractor-item">
          <div className="contractor-icon">
            <img
              src={GetImages.SR_CalendarIcon}
              alt="success"
              width="22"
              height="22"
              className="iconStyleWithWhiteFilter"
            />
          </div>
          <div>
            <div className="contractor-label">Assigned On</div>
            <div className="contractor-value">
              {formattedData?.RequestDateDateString || "N/A"}
            </div>
          </div>
        </div>
        {shouldShowEditReassign && canWrite("ServiceRequest") && (
          <SimpleOutlineButton
            className="edit-reassign-btn"
            label={"Edit/Reassign"}
            onClick={handleEditReassign}
          />
        )}
      </div>
    );
  };

  const renderInProgressView = () => (
    <div className="in-progress-view">
      <div className="fix-details-container">
        {renderContractorInfo()}
        <div className="fix-details-card">
          <div className="fix-details-card-header">
            <h3>Fix Details</h3>
            {!isVerified &&
              canWrite("ServiceRequest") &&
              statusLoaded &&
              curentServiceRequestStatus <=
                ServiceRequestStatusesEnum.VerificationPending && (
                <SimpleOutlineButton
                  className="edit-reassign-btn"
                  label="Edit"
                  onClick={onEditEstimateClick}
                />
              )}

          </div>
          <div className="detail-section-body">
            <div className="detail-section">
              <div className="detail-item">
                <div className="detail-icon calendar-icon">
                  <img
                    src={GetImages.SR_CalendarIcon}
                    alt="success"
                    width="22"
                    height="22"
                    className="iconStyleWithWhiteFilter"
                  />
                </div>
                <div className="detail-content">
                  <h4>Fix Date</h4>
                  <p>{formattedData?.CompleteDateDateOnly || "N/A"}</p>
                </div>
              </div>
            </div>
            <div className="separator-horizontal" />
            <div className="detail-section">
              <div className="detail-item">
                <div className="detail-icon document-icon">
                  <img
                    src={GetImages.SR_DocIcon}
                    alt="success"
                    width="22"
                    height="22"
                    className="iconStyleWithWhiteFilter"
                  />
                </div>
                <div className="detail-content">
                  <h4>Fix Notes</h4>
                  <p>{editData?.fixNotes || "N/A"}</p>
                </div>
              </div>
            </div>
            <div className="separator-horizontal" />
            <div className="detail-section">
              <div className="detail-item">
                <div className="detail-icon photo-icon">
                  <img
                    src={GetImages.SR_ImageIcon}
                    alt="success"
                    width="22"
                    height="22"
                    className="iconStyleWithWhiteFilter"
                  />
                </div>
                <div className="detail-content">
                  <h4>Review Work Photos</h4>
                  {isPageLoading ? (
                    // <p>Loading photos...</p>
                    <></>
                  ) : workPhotos.length > 0 ? (
                    <div className="photo-grid">
                      {workPhotos.map((photoSrc, index) => (
                        <img
                          key={index}
                          src={photoSrc}
                          alt={`Work photo ${index + 1}`}
                        />
                      ))}
                    </div>
                  ) : (
                    <p>No photos available.</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="payment-details-card">
        <div className="fix-details-card-header">
          <h3>Payment Details</h3>
        </div>
        <div className="fix-details-card-body">
          <div className="payment-row-sr">
            <span>Actual Cost</span>
            <CurrencyTooltip
              content={
                formattedData?.LaborCostRaw || editData?.laborCost || "0"
              }
              formatted={formattedData?.LaborCost || editData?.laborCost || "0"}
            >
              <span className="amount">
                {formattedData?.LaborCost || editData?.laborCost || "0"}
              </span>
            </CurrencyTooltip>
          </div>
          <div className="payment-row-sr">
            <span>Material Cost</span>
            <CurrencyTooltip
              content={
                formattedData?.MaterialCostRaw || editData?.materialCost || "0"
              }
              formatted={
                formattedData?.MaterialCost || editData?.materialCost || "0"
              }
            >
              <span className="amount">
                {formattedData?.MaterialCost || editData?.materialCost || "0"}
              </span>
            </CurrencyTooltip>
          </div>
          <div className="separator-horizontal" />
          <div className="payment-row-sr total-row">
            <span>Total Cost</span>
            <CurrencyTooltip
              content={
                formattedData?.TotalCostRaw || editData?.laborCost || "0"
              }
              formatted={formattedData?.TotalCost || editData?.laborCost || "0"}
            >
              <span className="amount">{formattedData?.TotalCost}</span>
            </CurrencyTooltip>
          </div>
        </div>
        <div className="fix-details-card-footer">
          {!isVerified && canWrite("ServiceRequest") &&

            (serviceRequestStatus === "Verification Pending" ||
              serviceRequestStatus === "Payment Pending") && (
              <SimpleButton
                width="100%"
                label={actionButtonLabel}
                onClick={onVerifyAction}
              />
            )}
        </div>
      </div>
    </div>
  );

  if (showInProgressView) {
    return renderInProgressView();
  }

  if (editMode || showAssignmentView) {
    return (
      <FormProvider {...addEstimateMethods}>
        <form id="edit-form" onSubmit={handleAddSubmit(onSubmit, onError)}>
          <div className="edit-form-view">
            {(showAssignmentView || editMode) && renderContractorInfo()}
            {canWrite("ServiceRequest") && renderWorkDetailsForm()}
            {canWrite("ServiceRequest") && (
              <div className="bottom-actions-full-width">
                <SimpleButton
                  label={isLoading ? "Processing..." : "Mark As Fixed"}
                  type="submit"
                  isDisabled={isLoading}
                />
              </div>
            )}
          </div>
        </form>
      </FormProvider>
    );
  }


  return (
    <FormProvider {...addEstimateMethods}>
      <div className="estimate-container">
        {error && <div className="error-message">{error}</div>}
        {canWrite("ServiceRequest") && (
          <div className="form-section">
            <h1 className="form-title">Add Estimate(s)</h1>
            <SelectField
              name="contractor"
              label="Contractor"
              required={true}
              options={contractorsLookup.map((c) => ({
                Id: c.id || "",
                Name: c.name || "",
              }))}
              placeholderText="Select Contractor"
              disabled={isLoading}
              className="mb-3"
              isSearchable={true}
            />
            <CurrencyInput
              name="estimateAmount"
              label="Estimate"
              required={true}
              placeholder="Enter Estimate"
              disabled={isLoading}
            />
            <SimpleButton
              className="btnAddEstimate w-100 mt-2"
              label={isLoading ? "Add Estimate" : "Add Estimate"}
              onClick={handleAddEstimate}
              isDisabled={isLoading || !selectedContractor || !estimateAmount}
            />
          </div>
        )}
        <div
          className={`estimates-section ${associatedContractors.length === 0 ? "no-bg" : ""}`}
        >
          <h2 className="estimates-title">Estimate(s)</h2>
          {isLoading ? (
            <div className="loading-spinner">
              <SimpleSpinner myClassName={"Icon50 mt-5"} />
            </div>
          ) : (
            <div className="estimates-list">
              {associatedContractors.length === 0 ? (
                <EmptyState
                  icon={estimateIcon}
                  heading="No Estimate(s) Added"
                  isButton={false}
                  isBorder={false}
                />
              ) : (
                associatedContractors.map((estimate) => (
                  <div key={estimate.id} className="estimate-item">
                    <div className="estimate-info">
                      <h3 className="estimate-name">
                        {estimate.contractorName}
                      </h3>
                      <p className="estimate-type">
                        {estimate.isInHouse ? "In House" : "Third Party"}
                      </p>
                    </div>

                    <div className="estimate-actions">
                      <span className="estimate-amount">
                        {estimate?.formatted?.EstimatedCost
                          ? `${currencySymbol}${estimate?.formatted?.EstimatedCost}`
                          : "-"}
                      </span>

                      <div className="dropdown-menu-new">
                        <ActionMenu
                          menuItems={menuItems}
                          Id={`${estimate.id}`}
                          trigger={
                            <button type="button" className="action-button">
                              <img
                                src={dropdownIcon}
                                alt="More options"
                                width={20}
                                height={20}
                                className="action-icon"
                              />
                            </button>
                          }
                        />
                      </div>
                    </div>

                    {showConfirmation === estimate.id && (
                      <ConfirmationPopup
                        isOpen={true}
                        title="Delete Estimate"
                        message="Do you want to delete this estimate?"
                        primaryButtonLabel="Delete"
                        secondaryButtonLabel="Cancel"
                        onPrimaryAction={() => handleDelete(estimate.id!)}
                        onSecondaryAction={() => setShowConfirmation(null)}
                        onClose={() => setShowConfirmation(null)}
                        isPrimaryDestructive={true}
                      />
                    )}
                  </div>
                ))
              )}
            </div>
          )}
        </div>
        {isEditModalOpen && (
          <RightSideModal
            isOpen={isEditModalOpen}
            onClose={() => {
              setIsEditModalOpen(false);
              setEditingEstimate(null);
              resetEditForm();
            }}
            title="Edit Estimate"
            buttonlabel="Save Changes"
            formId="edit-estimate-form"
            maxWidth={550}
            isDirty={editEstimateMethods.formState.isDirty}
          >
            <Card className="no-bg">
              <FormProvider {...editEstimateMethods}>
                <form
                  id="edit-estimate-form"
                  onSubmit={handleEditSubmit(handleUpdateEstimate, onError)}
                >
                  <div className="row">
                    <div className="col-md-6">
                      <InputField
                        name="contractorName"
                        label="Contractor"
                        required={true}
                        value={editingEstimate?.contractorName || ""}
                        disabled={true}
                        className="form-group"
                      />
                    </div>
                    <div className="col-md-6">
                      <CurrencyInput
                        name="estimatedCost"
                        label="Estimate"
                        required={true}
                        placeholder="Enter Estimate"
                        disabled={isLoading}
                        className="form-group"
                      />
                    </div>
                  </div>
                </form>
              </FormProvider>
            </Card>
          </RightSideModal>
        )}
      </div>
    </FormProvider>
  );
}
